<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import SectionLayout from './SectionLayout.vue';
import BusinessDay from '../../inbox/components/BusinessDay.vue';
import {
  timeSlotParse,
  timeSlotTransform,
  defaultTimeSlot,
  timeZoneOptions,
} from '../../inbox/helpers/businessHour';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';

const DEFAULT_TIMEZONE = {
  label: 'Brasilia Time (GMT-03:00)',
  value: 'America/Sao_Paulo',
};

export default {
  components: {
    SectionLayout,
    BusinessDay,
    NextButton,
    Policy,
  },
  setup() {
    const { accountId } = useAccount();
    return { accountId };
  },
  data() {
    return {
      timeZone: DEFAULT_TIMEZONE,
      dayNames: {
        0: 'Domingo',
        1: 'Segunda',
        2: 'Terça',
        3: 'Quarta',
        4: 'Quinta',
        5: 'Sexta',
        6: 'Sábado',
      },
      timeSlots: [...defaultTimeSlot],
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
    }),
    hasError() {
      // timeSlots is now an array of arrays (one array per day)
      return this.timeSlots.some(daySlots =>
        daySlots.some(slot => slot.from && !slot.valid)
      );
    },
    timeZones() {
      return [...timeZoneOptions()];
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
    accountWorkingHours() {
      return this.currentAccount.working_hours || [];
    },
    accountTimezone() {
      return (
        this.currentAccount.custom_attributes?.timezone ||
        this.currentAccount.timezone
      );
    },
  },
  watch: {
    currentAccount: {
      handler() {
        this.setDefaults();
      },
      deep: true,
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const timeSlots = this.accountWorkingHours.length
        ? timeSlotParse(this.accountWorkingHours)
        : defaultTimeSlot;
      this.timeSlots = timeSlots;
      this.timeZone =
        this.timeZones.find(item => this.accountTimezone === item.value) ||
        DEFAULT_TIMEZONE;
    },
    onSlotUpdate(slotIndex, slotData) {
      this.timeSlots = this.timeSlots.map((item, index) =>
        index === slotIndex ? slotData : item
      );
    },
    async updateAccountWorkingHours() {
      try {
        const workingHours = timeSlotTransform(this.timeSlots);
        const payload = {
          working_hours: workingHours,
        };

        // Update timezone if changed
        if (this.timeZone.value !== this.accountTimezone) {
          payload.timezone = this.timeZone.value;
        }

        // Update account with working hours and timezone
        await this.$store.dispatch('accounts/update', payload);

        // Refresh account data to get updated working hours
        await this.$store.dispatch('accounts/get', this.accountId);

        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <SectionLayout
    :title="$t('INBOX_MGMT.BUSINESS_HOURS.TITLE')"
    :description="$t('INBOX_MGMT.BUSINESS_HOURS.DESCRIPTION')"
    with-border
  >
    <Policy :permissions="['administrator']">
      <form @submit.prevent="updateAccountWorkingHours">
        <div class="mb-6">
          <div class="timezone-input-wrap">
            <label>
              {{ $t('INBOX_MGMT.BUSINESS_HOURS.TIMEZONE_LABEL') }}
            </label>
            <multiselect
              v-model="timeZone"
              :options="timeZones"
              deselect-label=""
              select-label=""
              selected-label=""
              track-by="value"
              label="label"
              close-on-select
              :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
              :allow-empty="false"
            />
          </div>

          <label>
            {{ $t('INBOX_MGMT.BUSINESS_HOURS.WEEKLY_TITLE') }}
          </label>
          <BusinessDay
            v-for="(daySlots, index) in timeSlots"
            :key="index"
            :day-name="dayNames[index]"
            :time-slots="daySlots"
            @update="data => onSlotUpdate(index, data)"
          />
        </div>
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.BUSINESS_HOURS.UPDATE')"
          :is-loading="uiFlags.isUpdating"
          :disabled="hasError"
        />
      </form>
    </Policy>
  </SectionLayout>
</template>

<style lang="scss" scoped>
.timezone-input-wrap {
  &::v-deep .multiselect {
    @apply mt-2;
  }
}
</style>
